import { connect, ConnectedProps } from 'react-redux'
import InputTableLeftColumn, { InputTableLeftColumnCompleteRequiredProps } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn'
import withLoading from '../../../../../../../../../../utils/hoc/withLoading'
import useQuoteOrderHeaderUrls from '../../../../../../../../../../utils/hooks/quotes/useQuoteOrderHeaderUrls'
import { ComponentTabObject } from '../../../../../../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../../../../utils/reducers'
import { CostSellPayload } from '../../../../../../../../../../utils/reducers/company/costsSell'
import QuoteReadOnlyHeaderCosts from '../../../../../header/QuoteReadOnlyHeaderCosts'
import QuoteReadOnlyHeaderFinancialRows from '../../../../../header/QuoteReadOnlyHeaderFinancialRows'
import QuoteReadOnlyHeaderMain, { useQuoteReadOnlyHeaderMainLogic } from '../../../../../header/QuoteReadOnlyHeaderMain'
import QuoteReadOnlyHeaderShipping from '../../../../../header/QuoteReadOnlyHeaderShipping'
import QuoteReadOnlyHeaderAccounts from '../../../../../header/QuoteReadOnlyHeaderAccounts'
import { SellTagsPayload } from '../../../../../../../../../../utils/reducers/sells/sellTags'
import QuoteReadOnlyHeaderVariables from '../../../../../header/QuoteReadOnlyHeaderVariables'
import { SellVariablePayload } from '../../../../../../../../../../utils/reducers/company/sellVariables'
import { FormElement, FormRender } from '../../../../../../../../../../forms/FormRenderer/types'
import { QuoteOrderModelType } from '@teinor/erp/types/company/order/quote/quoteOrder'
import { InputWithLabelMargin } from '../../../../../../../../../../forms/Input'
import ITHeaderText from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import { displayDate } from '../../../../../../../../../../utils/globals/date'
import { returnClassOfSpanTemplate } from '../../../../../../../../../../utils/globals/tailwind'
import { ITHeaderOrderTagsNew } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components'
import InputTableTopHeader from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'
import AppStyleDecider from '../../../../../../../../../../baseComponents/AppsStyleDecider'
import { CustomSellSelectCampaign, CustomSellSelectProject } from '../../../../../../../../../../specificComponents/sells/components'
import { EmployeesPayload } from '../../../../../../../../../../utils/reducers/company/employees'
import { useOrderITHeaderHandleChangeLogic } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/useOrderITHeaderHandleChange'
import { ProductTypesPayload } from '../../../../../../../../../../utils/reducers/products/productTypes'

export type QuoteOrderWaitingResponseStepITHeaderProps = InputTableLeftColumnCompleteRequiredProps & ReduxQuoteOrderWaitingResponseStepITHeader & { }

const QuoteOrderWaitingResponseStepITHeader: React.FC<QuoteOrderWaitingResponseStepITHeaderProps> = ({ ...rest }) => {
    return (
        <AppStyleDecider DefaultComponent={QuoteOrderWaitingResponseStepITHeaderDefault} NewComponent={QuoteOrderWaitingResponseStepITHeaderNew} {...rest} />
    )
}

const QuoteOrderWaitingResponseStepITHeaderDefault: React.FC<QuoteOrderWaitingResponseStepITHeaderProps> = ({ sellTags, ...rest }) => {
    const { t } = useTwinTranslation()
    const orderHeaderURLs = useQuoteOrderHeaderUrls()
    const tabs: ComponentTabObject = {
        'main': {
            component: QuoteReadOnlyHeaderMain,
            text: t('dataQuote', 'Datos del presupuesto'),
            extraProps: { orderHeaderURLs, sellTags: sellTags?.allTags }
        },
        'financialRow': {
            component: QuoteReadOnlyHeaderFinancialRows,
            text: t('discountsAndRecharges', 'Descuentos y recargos'),
            permission: 'quote.order',
            extraProps: { orderHeaderURLs }
        },
        'costs': {
            component: QuoteReadOnlyHeaderCosts,
            text: t('costsQuote', 'Gastos del presupuesto'),
            permission: 'quote.order',
            extraProps: { orderHeaderURLs }
        },
        'shipping': {
            component: QuoteReadOnlyHeaderShipping,
            text: t('shipping', 'Transporte'),
            permission: 'quote.order',
            extraProps: { orderHeaderURLs }
        },
        'accounts': {
            component: QuoteReadOnlyHeaderAccounts,
            text: t('divideIntoChapters', 'Dividir en capítulos'),
            permission: 'quote.order',
            extraProps: { orderHeaderURLs }
        },
        'variables': {
            component: QuoteReadOnlyHeaderVariables,
            text: t('quoteVariables', 'Variables del presupuesto'),
            permission: 'quote.order',
        },
    }
    return (
        <InputTableLeftColumn tabs={tabs} {...rest} />
    )
}

const QuoteOrderWaitingResponseStepITHeaderNew: React.FC<QuoteOrderWaitingResponseStepITHeaderProps> = ({ sellTags, employees, ...rest }) => {
    const { t } = useTwinTranslation()
    const headerData = rest?.headerData
    const { getCustomerProjects, customerProjects, customerCampaigns, getCustomerCampaigns, memorizedAddresses, activeCurrencies } = useQuoteReadOnlyHeaderMainLogic({ headerData: rest?.headerData || {} })
    const { handleChange } = useOrderITHeaderHandleChangeLogic({ setCompleteData: rest?.setCompleteData, updateDataURL: rest?.updateDataURL })
    let extraFields: FormElement<keyof QuoteOrderModelType>[] = []
    if (headerData?.CustomerAddressId) {
        extraFields.push({
            name: 'CustomerAddressId',
            component: 'Custom',
            render: () => {
                return <ITHeaderText label={t('fiscalAddress', 'Dirección Fiscal')} value={memorizedAddresses.customerAddress} />
            },
            hideSectionByDefault: true
        })
    }
    const sections: FormRender<QuoteOrderModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('quoteN', 'Nº presupuesto')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA')} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('dateQuoteOrder', 'Fecha del presupuesto')} value={displayDate(new Date(value))} />
                },
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('representative', 'Representante')} value={headerData?.RepresentativeEmployee?.fullname_short} />
                },
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: (props) => {
                        return <InputWithLabelMargin name='custom_name' label={t('customName', 'Nombre personalizado')} onChange={props.onChange} defaultValue={headerData?.custom_name || ''} className='custom_name_input_field' />
                    }
                },
                {
                    name: 'CustomerProjectId',
                    component: 'Custom',
                    render: ({ extraProps, setLockOnClickOut }) => <CustomSellSelectProject getCompleteData={getCustomerProjects} handleChange={handleChange} headerData={headerData || {}} items={extraProps.customerProjects} orderByOpp={true} setLockOnClickOut={setLockOnClickOut} />,
                    extraProps: {
                        customerProjects: customerProjects || {}
                    }
                },
                {
                    name: 'CampaignCustomerId',
                    component: 'Custom',
                    render: ({ extraProps, setLockOnClickOut }) => <CustomSellSelectCampaign getCompleteData={getCustomerCampaigns} handleChange={handleChange} headerData={headerData || {}} items={extraProps.customerCampaigns} label={t('campaign', 'Campaña')} setLockOnClickOut={setLockOnClickOut} />,
                    extraProps: {
                        customerCampaigns: customerCampaigns || {}
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (
                            <ITHeaderOrderTagsNew getCompleteData={rest?.getCompleteData} dictTags={extraProps?.dictTags || []} className={returnClassOfSpanTemplate(3) + ' order_top_header_custom_select_tags'} selectedTags={extraProps.selectedTags} onChange={handleChange} headerData={headerData || {}} />)
                    },
                    extraProps: {
                        selectedTags: headerData?.tags,
                        dictTags: sellTags?.allTags
                    }
                },
                {
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: () => {
                        return <ITHeaderText label={t('workingCenterFiscalAddress', 'Dirección Fiscal centro de trabajo')} value={memorizedAddresses?.workingCenterAddress || ''} />
                    },
                    hideSectionByDefault: true
                },
                ...extraFields,
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('workingCenter', 'Centro de trabajo')} value={headerData?.WorkingCenter?.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={activeCurrencies?.[value]?.name} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: () => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie} />
                    },
                    hideSectionByDefault: true
                }
            ]
        }
    ]

    return (
        <InputTableTopHeader handleChange={handleChange} sections={sections} headerData={headerData} getCompleteData={rest?.getCompleteData} />
    )
}

const QuoteOrderWaitingResponseStepITHeaderDispatch = {
    setCostsSell: (payload: CostSellPayload) => ({ type: 'CHANGE_COSTSELL', payload }),
    setSellTags: (payload: SellTagsPayload) => ({ type: 'CHANGE_SELL_TAGS', payload }),
    setSellVariables: (payload: SellVariablePayload) => ({ type: 'CHANGE_SELLVARIABLE', payload }),
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
    setProductTypes: (payload: ProductTypesPayload) => ({ type: 'CHANGE_PRODUCT_TYPES', payload }),
}

export type ReduxQuoteOrderWaitingResponseStepITHeader = ConnectedProps<typeof quoteOrderWaitingResponseHeaderConnect>
const mapQuoteOrderWaitingResponseStepITHeaderConnector = (state: AllReduxPayloads) => ({ costsSell: state.costsSell, sellTags: state.sellTags, sellVariables: state.sellVariables, employees: state.employees, productTypes: state.productTypes })
const quoteOrderWaitingResponseHeaderConnect = connect(mapQuoteOrderWaitingResponseStepITHeaderConnector, QuoteOrderWaitingResponseStepITHeaderDispatch)
const QuoteOrderWaitingResponseStepITHeaderConnectLoading = withLoading(QuoteOrderWaitingResponseStepITHeader, [{ fetchUrl: '/api/app/config/cost/costSell/getAllCostSells', propName: 'costsSell', setFunctionName: 'setCostsSell' }, { fetchUrl: '/api/app/order/sell/sellTag/getAllSellTags', propName: 'sellTags', setFunctionName: 'setSellTags' }, { fetchUrl: '/api/app/config/sell/sellVariable/getAllSellVariables', propName: 'sellVariables', setFunctionName: 'setSellVariables' }, { fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }, { fetchUrl: '/api/app/product/productType/getAllProductTypes', propName: 'productTypes', setFunctionName: 'setProductTypes' }])
const QuoteOrderWaitingResponseStepITHeaderConnect = quoteOrderWaitingResponseHeaderConnect(QuoteOrderWaitingResponseStepITHeaderConnectLoading)

export default QuoteOrderWaitingResponseStepITHeaderConnect